import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';

import AboutSources from './AboutSections/AboutSources';
import AboutAttributes from './AboutSections/Attributes/AboutAttributes';
import AboutGeneTypes from './AboutSections/AboutGeneTypes';
import AboutVariantTypes from './AboutSections/Attributes/Variants/VariantTypes';
import AboutSchema from './AboutSections/AboutSchema';
import AboutMetrics from './AboutSections/Metrics/AboutMetrics';
import AboutOntology from './AboutSections/AboutOntology';
import AboutCiteUs from './AboutSections/AboutCiteUs';
import Loader from '../Common/Loader';

import StatisticsDiagram from './components/statistics/StatisticsDiagram';
import Changelog from './components/statistics/Changelog';
import { StatsTxt } from './components/statistics/StatisticsText';
import { StatsJSON, VersionsArray, VersionInfo } from './types';
import { StatisticsTable } from './components/statistics/StatisticsTables';
import { formatVersion, toHashLabel, compareVersions } from './helpers';
import { releasesApiService } from '../../services/releasesApiService';
import { apiReleaseToVersionsArray } from './releaseApiMappers';
import AboutSidebar from './components/elements/AboutSidebar';
import SideBarLayout from '../Layout/SideBarLayout';
import BackgroundPages from '@/global/Background/BackgroundPages';

function AboutContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statistics, setStats] = useState<StatsJSON | null>(null);
  const [apiVersions, setApiVersions] = useState<VersionsArray[]>([]);
  const [loading, setLoading] = useState(true);

  // Sidebar state
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);

  // Redirigir si no hay sección ni versión
  useEffect(() => {
    const hasSection = searchParams.has('section');
    const hasVersion = searchParams.has('version');

    if (!hasSection && !hasVersion) {
      navigate(`${location.pathname}?section=gdaStatistics`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  // Cargar stats y versiones API
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      let statsData: StatsJSON | null = null;

      try {
        const res = await fetch('/stats.json');
        statsData = res.ok ? ((await res.json()) as StatsJSON) : { current: '' };
      } catch (error) {
        console.error('❌ Error loading stats.json:', error);
        statsData = { current: '' };
      }

      if (cancelled) return;
      setStats(statsData);

      try {
        const list = await releasesApiService.getReleasesList();
        const versionKeysFromStats = new Set(Object.keys(statsData || {}).filter(k => k !== 'current'));
        const versionsToFetch = list.map(r => r.version).filter(v => !versionKeysFromStats.has(v));
        const details = await Promise.all(
          versionsToFetch.map(version => releasesApiService.getRelease(version)),
        );
        if (cancelled) return;
        const fromApi = details.map(apiReleaseToVersionsArray);
        setApiVersions(fromApi);
      } catch (error) {
        console.error('❌ Error loading releases from API:', error);
        setApiVersions([]);
      }

      if (!cancelled) setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !statistics) {
    return <Loader />;
  }

  // Preparar versiones
  const versionsFromStats: VersionsArray[] = Object.entries(statistics)
    .filter(([key]) => key !== 'current')
    .map(([key, value]) => (typeof value === 'string' ? null : { ...(value as VersionInfo), version: key }))
    .filter((v): v is VersionsArray => v !== null);

  const statsVersionSet = new Set(versionsFromStats.map(v => v.version));
  const apiOnly = apiVersions.filter(v => !statsVersionSet.has(v.version));
  const versionsArray: VersionsArray[] = [...versionsFromStats, ...apiOnly].sort((a, b) =>
    compareVersions(b.version as string, a.version as string),
  );

  const currentVersion = versionsArray.length > 0 ? versionsArray[0].version : statistics.current || '';
  const section = searchParams.get('section');
  const versionParam = searchParams.get('version');

  const activeVersionObj = versionParam
    ? versionsArray.find(v => toHashLabel(v.version as string) === versionParam)
    : (versionsArray.find(v => v.version === currentVersion) ?? versionsArray[0] ?? null);

  const showVersionDetails = Boolean(versionParam && activeVersionObj);

  if (versionsArray.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">No version history data available.</p>
      </section>
    );
  }

  return (
    <MathJaxContext>
      <div className="apply-trial-page">
        <BackgroundPages />
        <section className="relative z-10 px-2 lg:px-14 md:mt-24 mt-20">
          <h1 className="sr-only">Information about database statistics, attributes and metrics.</h1>

          <div className="flex flex-col lg:flex-row lg:gap-8 lg:p-14">
            {/* Sidebar */}
            <SideBarLayout>
              <AboutSidebar
                versionsArray={versionsArray}
                onClick={() => setSidebarOpenMobile(false)}
                collapsed={false}
              />
            </SideBarLayout>

            {/* Content */}
            <div className="w-full lg:w-3/4 lg:flex-grow">
              <div className="tab-content" id="dbInfoTabbedPane">
                {showVersionDetails ? (
                  <div className="space-y-6">
                    {activeVersionObj?.releaseDate && (
                      <h2 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                        {activeVersionObj.releaseDate}: Release of version{' '}
                        {formatVersion(activeVersionObj.version)} of DISGENET
                      </h2>
                    )}
                    {activeVersionObj && Object.keys(activeVersionObj.statistics).length > 0 && (
                      <StatsTxt version={activeVersionObj.version} statistics={activeVersionObj.statistics} />
                    )}
                    {activeVersionObj && (
                      <Changelog
                        version={activeVersionObj.version}
                        releaseDate={activeVersionObj.releaseDate}
                        statistics={activeVersionObj.statistics}
                        changelog={activeVersionObj.changelog}
                        currentVersion={currentVersion}
                      />
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {section === 'gdaStatistics' && (
                      <div id="gdaStatistics">
                        <h2>Statistics</h2>
                        <div className="mb-2 divider" />
                        <div className="space-y-6 sm:space-y-8">
                          <StatsTxt
                            statistics={activeVersionObj!.statistics}
                            version={activeVersionObj!.version}
                          />
                          <StatisticsDiagram statistics={activeVersionObj!.statistics} />
                          <StatisticsTable />
                        </div>
                      </div>
                    )}
                    {section === 'gene_type' && <AboutGeneTypes />}
                    {section === 'variant_type' && <AboutVariantTypes />}
                    {section === 'schema' && <AboutSchema />}
                    {section === 'sources' && <AboutSources />}
                    {section === 'metrics' && <AboutMetrics />}
                    {section === 'attributes' && <AboutAttributes />}
                    {section === 'assoctypeont' && <AboutOntology />}
                    {section === 'cite_us' && <AboutCiteUs />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </MathJaxContext>
  );
}

export default AboutContent;
